"use client";

import { useInspectorMode } from "@utils/ContentfulUtils";
import { db } from "@utils/IndexedDb";
import { useLiveQuery } from "dexie-react-hooks";
import { AnimatePresence, motion } from "framer-motion";
import dynamic from "next/dynamic";
import { getImageProps } from "next/image";
import Image from "next/image";
import React, { useContext, useEffect, useRef, useState } from "react";

import { NavigationContext } from "@/context/NavigationContext";
import { searchInteractionTracking } from "@/dataLayer/dataLayer";
import useBreakPoint from "@/hooks/useBreakpoint";
import useIsSaleScreen from "@/hooks/useIsSaleScreen";
import useMediaQuery from "@/hooks/useMediaQuery";
import { BaseEntry, Media, PRODUCT_TYPE, VISUAL_VARIANT } from "@/types";

const FilterBar = dynamic(() => import("@/components/ui/Form/Filters/FilterBar"), { ssr: false });
export interface HeroFirstCard extends BaseEntry {
  video?: Media;
  video_mobile?: Media;
  backupVideoImage: Media;
  image?: Media;
  mobileImage?: Media;
  logo?: Media;
  logoScrollDownImage?: Media;
  displaySearchBar?: boolean;
  startDateTime?: any;
  endDateTime?: any;
  scheduleEnabled?: boolean;
}

interface Props {
  entry: HeroFirstCard;
}

export default function SalesHeroFirstCard({ entry }: Props) {
  const isSo = useIsSaleScreen();
  const inspectorMode = useInspectorMode(entry.id);
  const { isMd } = useBreakPoint("md");
  const { setDesktopPanelID } = useContext(NavigationContext);
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  const propertyTypeRecords: number | undefined = useLiveQuery(async () => {
    const projectCount = await db?.PROPERTY_TYPE?.count();
    return projectCount || 0;
  });

  const poster = getImageProps({
    alt: "",
    width: 1024,
    height: 768,
    quality: 80,
    src: entry?.backupVideoImage?.url,
  });

  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = true;
      videoRef.current.defaultMuted = true;
      videoRef.current.playsInline = true;

      // Play the video manually if autoplay fails
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.warn("Autoplay failed:", error);
        });
      }
    }
  }, []);

  if (!isSo) return null;

  return (
    <div className="relative flex justify-center h-svh" {...inspectorMode.getProps("video")}>
      <div className="absolute z-10 w-full h-full img-overlay-homepage-hero"></div>
      {entry?.video && (
        <video className="videoHero" poster={poster.props.src} autoPlay loop muted playsInline ref={videoRef}>
          {isDesktop && <source media="all and (orientation: landscape)" src={entry?.video?.url} type="video/mp4" />}
          {!isDesktop && entry?.video_mobile && <source media="all and (orientation: portrait)" src={entry?.video_mobile?.url} type="video/mp4" />}
        </video>
      )}
      {!entry.video && (
        <Image
          src={isMd ? entry?.image?.url || "" : entry.mobileImage?.url || ""}
          width={isMd ? entry?.image?.width : entry?.mobileImage?.width}
          height={isMd ? entry?.image?.height : entry?.mobileImage?.height}
          className="h-full w-full"
          alt={entry?.image?.title || ""}
        />
      )}
      {entry?.logoScrollDownImage && (
        <div className="absolute bottom-3xl left-1/2 -translate-x-1/2 -translate-y-1/2 mb-xl">
          <div className="flex flex-col items-center mt-[20px] ijaz">
            <Image src={entry?.logoScrollDownImage?.url} alt={entry?.logoScrollDownImage?.title} width={177} height={90} />
          </div>
        </div>
      )}
      {entry?.logo ? (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 mb-xl">
          <div className="flex-col items-center hidden md:flex">
            <Image src={entry?.logo?.url} alt={entry?.logo?.title} width={310} height={157} />
          </div>
          <div className="flex flex-col items-center md:hidden">
            <Image src={entry?.logo?.url} alt={entry?.logo?.title} width={177} height={90} />
          </div>
          {/* {entry?.logoScrollDownImage && (
            <div className="flex flex-col items-center mt-[20px]">
              <Image src={entry?.logoScrollDownImage?.url} alt={entry?.logoScrollDownImage?.title} width={177} height={90} />
            </div>
          )} */}
        </div>
      ) : entry?.displaySearchBar ? (
        <AnimatePresence>
          {!!propertyTypeRecords && propertyTypeRecords > 0 && (
            <motion.div
              className="absolute left-1/2 bottom-xl z-10 w-22\/24"
              initial={{ opacity: 0, y: 50, x: "-50%" }}
              animate={{ opacity: 1, y: 0, x: "-50%" }}
              transition={{ duration: 1 }}
              onMouseDown={(e) => {
                if (isMd) {
                  searchInteractionTracking();
                }
              }}
            >
              <div>
                <FilterBar name="hero-first-card" filters={[PRODUCT_TYPE.PROPERTY_TYPE, "bedrooms", "price"]} variant={VISUAL_VARIANT.DARK} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      ) : (
        <></>
      )}
    </div>
  );
}
